import Promise from 'bluebird';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEqual, omit, some, keys, compact } from 'underscore';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import { getParameter, getDomain } from '@bingads-webui/url-util';
import { getImageSizeLimitation, getTextSizeLimitation, MaxBusinessNameLength, loadImagePromise } from './util';
import { usePreviewState, panelNames } from './preview/state';

// for testing
const isSelenium = getParameter('isselenium');
const isLocal = getDomain(window.location.href) === 'localhost';
const mock = isSelenium === 'true' || isLocal;

const useRecommendations = ({ copilotDataService }) => {
  const { editVideoAdsAssetRecommendations, fetchVideoKeyFrameRecommendations } = copilotDataService;

  const generateVideo = ({ video, scenes, videoSettings }) => {
    const {
      template,
    } = video;
    const {
      templateId,
      audio,
    } = template;
    const { logo, businessName } = videoSettings;
    const newVideoMetaData = {
      template: {
        templateId,
        logo,
        businessName,
        audio,
        scenes: scenes.map(scene => ({
          text: scene.text,
          images: scene.images,
        })),
      },
    };
    const ads = newVideoMetaData;

    return editVideoAdsAssetRecommendations(ads, mock)
      .then((res) => {
        if (res.data && res.data.ads && res.data.ads.length > 0) {
          return res.data.ads[0];
        }
        return Promise.reject(res);
      });
  };

  const generateScene = ({ logo, businessName, scene }) => {
    const newSceneMetaData = {
      text: scene.text,
      images: scene.images,
      keyFrame: {
        templateId: scene.keyFrame.templateId,
      },
    };

    const requestData = {
      logo,
      businessName,
      scene: newSceneMetaData,
    };

    return fetchVideoKeyFrameRecommendations(requestData, mock)
      .then((res) => {
        if (res && res.data && res.data.scene) {
          return res.data.scene;
        }
        return Promise.reject(res);
      });
  };

  return {
    generateVideo,
    generateScene,
  };
};

// even the same scene, the keyFrame url could be different.
const isEqualScene = (scene1, scene2) => isEqual(omit(scene1, 'keyFrame'), omit(scene2, 'keyFrame'));
const isSceneChanged = scene => (scene.effective ? !isEqualScene(scene.updating, scene.effective) : !isEqualScene(scene.original, scene.updating));
const isVideoSettingsChanged = videoSettings => !isEqual(videoSettings.effective, videoSettings.updating);
const isVideoChanged = (scenes, videoSettings) => scenes.some(scene => isSceneChanged(scene)) || isVideoSettingsChanged(videoSettings);

// For the scene related state, we could save them into a single scene object, and then use the id to locate the scene object.
// Text, Image, KeyFrame are the main scene properties that could be changed. (KeyFrame is from the API call. Text and Image are also included in the API calls.)
// Original, Updating and Effective are for checking if the scene has been changed.
// For the video, we could save the video URL and it's related scenes.
// To check if the video could be generated, we could check if the scenes have been changed.
// If the scenes have been changed, we could generate the video.
// If the scenes are not changed, we could apply the video and it's related scenes (if we want to save the metadata).

// videoMetaData is the original video.
export const useEditVideoAdsViewState = ({
  videoMetaData,
  dataService,
  copilotDataService,
  onApply,
  setIsOpen,
  logger,
  i18n,
  newImageDataObject,
  newLogoDataObject,
  saveVideoWhenApply,
  isCTV,
}) => {
  const {
    uploadVideoMetadata,
  } = dataService;

  const {
    generateScene,
    generateVideo,
  } = useRecommendations({
    copilotDataService,
  });

  const [scenes, setScenes] = useState([]);
  const [selectedSceneId, setSelectedSceneId] = useState(-1); // Used to locate teh scene with id
  const [isApplying, setIsApplying] = useState(false); // Used to show the applying status
  const [video, setVideo] = useState({});
  const [originalVideo, setOriginalVideo] = useState(videoMetaData.url);
  const [videoSettings, setVideoSettings] = useState({}); // Used to store the video settings [logo, businessName]
  const [sceneGenerated, setSceneGenerated] = useState(1);
  const [videoGenerated, setVideoGenerated] = useState(1);
  const [videoScenesCache, setVideoScenesCache] = useState([]);
  const [generateError, setGenerateError] = useState(undefined); // Used to show the error message
  const [haveEmptyImage, setHaveEmptyImage] = useState(false);
  const [newImageObject, setNewImageObject] = useState(newImageDataObject);

  const {
    currentPanel,
    isScenePanel,
    setCurrentPanel,
  } = usePreviewState();

  useEffect(() => {
    if (videoMetaData) {
      // initialization from the video metadata
      const { scenes: scenesInner = [], logo, businessName } = videoMetaData && videoMetaData.template;
      const scenesWithIds = scenesInner.map((scene, index) => ({
        original: scene,
        updating: scene, // Used to keep track of the scene changes (the react view will modify this part)
        effective: scene, // Assume, we've already generated the scene
        id: index,
        newImage: { url: scene.images[0], id: '' },
      }));

      setScenes(scenesWithIds);
      setSelectedSceneId(-1);
      setOriginalVideo(videoMetaData.url);
      setVideo({
        ...videoMetaData,
        scenes: scenesWithIds,
      });
      const videoSettingsInner = {
        logo,
        businessName,
      };
      setVideoSettings({
        original: videoSettingsInner,
        updating: videoSettingsInner,
        effective: videoSettingsInner,
      });
      if (isCTV) {
        logger.logTrace('CTV Copilot video editor: editor panel opened');
      } else {
        logger.logTrace('OLV Copilot video editor: editor panel opened');
      }
    }
  }, [logger, videoMetaData]);

  const generateNewVideo = useCallback((scenesInCall, isSwitchFromScene) => {
    if (isSwitchFromScene) {
      setVideoGenerated(10);
    } else {
      setVideoGenerated(0);
    }
    setGenerateError(undefined);

    return generateVideo({ scenes: scenesInCall.map(scene => scene.updating), video, videoSettings: videoSettings.updating }).then((newVideoMetaData) => {
      const { scenes: scenesInner = [], logo, businessName } = newVideoMetaData && newVideoMetaData.template;
      const scenesWithIds = scenesInCall.map((scene, index) => ({
        ...scene,
        effective: scenesInner[index],
        updating: scenesInner[index],
        id: index,
      }));

      setScenes(scenesWithIds);
      setVideo({
        ...newVideoMetaData,
        scenes: scenesWithIds,
      });
      const newVideoSettings = { logo, businessName };
      setVideoSettings({
        ...videoSettings,
        effective: newVideoSettings,
        updating: newVideoSettings,
      });
      setVideoGenerated(1);
      setVideoScenesCache([]);
      if (isCTV) {
        logger.logTrace('CTV Copilot video editor: video generated');
      } else {
        logger.logTrace('OLV Copilot video editor: video generated');
      }
      return {
        ...newVideoMetaData,
        scenes: scenesWithIds,
        msg: 'success',
      };
    }).catch((error) => {
      const { errors } = error;
      if (errors && errors[0] && errors[0].message) {
        setGenerateError(errors[0].message);
      } else {
        setGenerateError(i18n.getString(_TL_('Copilot has failed to generate a new video. Please try again later.')));
      }
      setVideoGenerated(-1);
      return {
        msg: 'failed',
      };
    });
  }, [generateVideo, video, videoSettings, logger, i18n]);

  const delayedVideoGeneration = useCallback(() => {
    if (videoScenesCache.length > 0) {
      const promise = generateNewVideo(videoScenesCache, true);
      return promise;
    }
    return Promise.resolve({ ...video, msg: 'success' });
  }, [generateNewVideo, video, videoScenesCache]);

  const selectedScene = useMemo(() => scenes && scenes.find(scene => scene.id === selectedSceneId), [scenes, selectedSceneId]) || {
    original: {},
    updating: {},
    effective: {},
    id: -1,
  };

  const onSceneSelected = useCallback((scene) => {
    setSelectedSceneId(scene.id);
  }, []);

  const onScenesUpdated = useCallback((updatedScenes) => {
    setScenes(updatedScenes);
  }, []);

  const onVideoSettingsSelected = useCallback(() => {
    setSelectedSceneId(-1);
  }, []);

  const handleGenerateVideo = useCallback(() => {
    generateNewVideo(scenes, false);
  }, [generateNewVideo, scenes]);

  const generateNewScene = useCallback(({ scene, videoLevelMetaData }) => {
    if (isSceneChanged(scene)) {
      return generateScene({
        scene: scene.updating,
        logo: videoLevelMetaData.logo,
        businessName: videoLevelMetaData.businessName,
      });
    }
    return scene.effective ? Promise.resolve(scene.effective) : Promise.resolve(scene.original);
  }, [generateScene]);

  const handleGenerateScene = useCallback(() => {
    setSceneGenerated(0);
    setGenerateError(undefined);

    // As user could switch between the scenes, we need to generate all the scenes.
    Promise.all(scenes.map(scene => generateNewScene({ scene, videoLevelMetaData: videoSettings.updating }))).then((newScenes) => {
      const copyScenes = Array.from(scenes);
      const newScenesWithIds = newScenes.map((newScene, index) => {
        const co = copyScenes[index];
        copyScenes[index] = {
          ...co,
          effective: {
            ...newScene,
          },
          updating: { ...newScene },
        };
        return copyScenes[index];
      });
      setScenes(newScenesWithIds);
      setVideoScenesCache(newScenesWithIds);
      setVideoSettings({
        ...videoSettings,
        effective: videoSettings.updating,
      });
      setSceneGenerated(1);
      logger.logTrace('Copilot video editor: scene generated');
    }).catch((error) => {
      const { errors } = error;
      if (errors && errors[0] && errors[0].message) {
        setGenerateError(errors[0].message);
      } else {
        setGenerateError(i18n.getString(_TL_('Copilot has failed to generate a new scene. Please try again later.')));
      }
      setSceneGenerated(-1);
    });
  }, [scenes, generateNewScene, videoSettings, logger, i18n]);

  const handleGenerate = useCallback(() => {
    logger.logTrace('Copilot video editor: generate button clicked');
    if (isScenePanel && !isVideoSettingsChanged(videoSettings)) {
      // Preserve the data for video because user might switch from scene to video panel and edit the scene at the same time.
      handleGenerateScene();
    } else {
      if (sceneGenerated !== 0) {
        handleGenerateScene();
      }
      handleGenerateVideo();
    }
  }, [logger, isScenePanel, videoSettings, handleGenerateScene, sceneGenerated, handleGenerateVideo]);

  const handleApply = useCallback(() => {
    setIsApplying(true);
    delayedVideoGeneration().then((data) => {
      const {
        msg,
        url: newVideoUrl,
        scenes: newScenes,
      } = data;
      if (msg === 'failed') {
        // will stay on the panel and show the error message
        setIsApplying(false);
        return;
      }
      if (originalVideo !== newVideoUrl) {
        // should apply and close the panel
        const savedImages = newScenes.map((scene) => {
          // image already saved to asset library
          if (scene.newImage && scene.newImage.id) {
            return scene.newImage;
          }
          return null;
        });
        if (saveVideoWhenApply) {
          uploadVideoMetadata({
            videoMetadata: omit(data, 'msg'), savedImages: compact(savedImages), savedLogo: newLogoDataObject, newVideoFromMTObject,
          })
            .then((appliedVideoWithId) => {
              onApply(appliedVideoWithId);
              setIsOpen(false);
              setIsApplying(false);
              logger.logTrace('Copilot video editor: video applied');
            })
            .catch(() => {
              setIsApplying(false);
              setGenerateError(i18n.getString(_TL_('We\'re unable to save the video. Please try again later.')));
            });
        } else {
          const newVideo = {
            ...omit(data, 'msg'),
            savedMaterial: {
              savedImages: compact(savedImages),
              savedLogo: newLogoDataObject,
            },
          };
          onApply(newVideo);
          setIsOpen(false);
          setIsApplying(false);
          logger.logTrace('Copilot video editor: video applied');
        }
      } else {
        setIsOpen(false);
        setIsApplying(false);
      }
    }).catch(() => {
      setIsApplying(false);
    });
  }, [delayedVideoGeneration, i18n, logger, newLogoDataObject, onApply, originalVideo, saveVideoWhenApply, setIsOpen, uploadVideoMetadata]);

  const switchPanel = useCallback((panel) => {
    setCurrentPanel(panel);
    if (panelNames.video === panel && videoGenerated !== 0 && videoGenerated !== 10 && videoScenesCache.length > 0) {
      generateNewVideo(videoScenesCache, true);
    }
  }, [generateNewVideo, setCurrentPanel, videoGenerated, videoScenesCache]);

  const textErrorMessages = useMemo(() => {
    const errorMessages = {};
    scenes.forEach((scene) => {
      errorMessages[scene.id] = [];
      const updatingText = scene.updating.text;
      const maxLengthOfText = getTextSizeLimitation(scene.updating.keyFrame.templateId);
      updatingText.forEach((text, index) => {
        if (text.length > maxLengthOfText[index]) {
          errorMessages[scene.id].push(i18n.getString(_TL_('Value cannot be longer than {{maxLength}} characters'), { maxLength: maxLengthOfText[index] }));
        } else if (text.length === 0) {
          errorMessages[scene.id].push(i18n.getString(_TL_('Required field')));
        } else {
          errorMessages[scene.id].push('');
        }
      });
    });

    return errorMessages;
  }, [i18n, scenes]);

  const haveTextError = useMemo(() => {
    const haveErrorInScenes = [];
    if (textErrorMessages) {
      keys(textErrorMessages).forEach((key) => {
        haveErrorInScenes.push(some(textErrorMessages[key]));
      });
    }
    return some(haveErrorInScenes);
  }, [textErrorMessages]);

  const textErrorForSelectedScene = useMemo(() => textErrorMessages[selectedSceneId], [selectedSceneId, textErrorMessages]);

  const businessNameErrorMessage = useMemo(() => {
    const businessName = videoSettings && videoSettings.updating && videoSettings.updating.businessName;
    if (businessName && businessName.text && businessName.text.length > MaxBusinessNameLength) {
      return i18n.getString(_TL_('Value cannot be longer than {{maxLength}} characters'), { maxLength: MaxBusinessNameLength });
    }
    return '';
  }, [i18n, videoSettings]);

  useEffect(() => {
    if (newImageDataObject && newImageDataObject.url) {
      setNewImageObject(newImageDataObject);
    }
  }, [newImageDataObject]);

  useEffect(() => {
    setNewImageObject(undefined);
  }, [selectedSceneId]);

  useEffect(() => {
    if (newImageObject && newImageObject.url) {
      setScenes(prevScenes => prevScenes.map((scene) => {
        if (scene.id === selectedSceneId) {
          return {
            ...scene,
            updating: {
              ...scene.updating,
              images: [newImageDataObject.url],
            },
            newImage: newImageDataObject,
          };
        }
        return scene;
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newImageObject, newImageDataObject]);

  useEffect(() => {
    const allImages = [];
    if (scenes.length > 0) {
      scenes.forEach((scene) => {
        allImages.push(...scene.updating.images);
      });
    }
    if (videoSettings && videoSettings.updating && videoSettings.updating.logo && videoSettings.updating.logo.display) {
      allImages.push(videoSettings.updating.logo.url);
    }
    const loadImagePromises = allImages.map(img => loadImagePromise(img, mock));

    Promise.all(loadImagePromises).then((results) => {
      if (results.includes('failed')) {
        setHaveEmptyImage(true);
      } else {
        setHaveEmptyImage(false);
      }
    });
  }, [scenes, videoSettings]);

  const disableGenerate = useMemo(() =>
    sceneGenerated === 0
    || videoGenerated === 0 || videoGenerated === 10
    || !(isVideoChanged(scenes, videoSettings))
    || haveTextError
    || businessNameErrorMessage !== ''
    || haveEmptyImage, [businessNameErrorMessage, haveEmptyImage, haveTextError, sceneGenerated, scenes, videoGenerated, videoSettings]);

  // If user modified the scenes and did not click the generate button, we could not apply the button.
  const disableApply = useMemo(() => sceneGenerated === 0 || videoGenerated === 0 || videoGenerated === 10
    || isVideoChanged(scenes, videoSettings), [sceneGenerated, scenes, videoGenerated, videoSettings]);

  const minSizeOfImage = useMemo(() => {
    if (selectedScene && selectedScene.updating && selectedScene.updating.keyFrame) {
      return getImageSizeLimitation(selectedScene.updating.keyFrame.templateId);
    }
    return [];
  }, [selectedScene]);

  return {
    video,
    setVideo,
    selectedSceneId,
    setSelectedSceneId,
    scenes,
    onSceneSelected,
    setScenes,
    selectedScene,
    onScenesUpdated,
    sceneGenerated,
    videoGenerated,
    disableApply,
    disableGenerate,
    isApplying,
    handleGenerate,
    handleApply,
    currentPanel,
    isScenePanel,
    switchPanel,
    generateError,
    setGenerateError,
    setNewImageObject,
    textErrorForSelectedScene,
    minSizeOfImage,
    onVideoSettingsSelected,
    videoSettings,
    setVideoSettings,
    businessNameErrorMessage,
  };
};
